import React, { useEffect, useState, useCallback, Suspense, lazy, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShift } from '../../context/ShiftContext';
import { useShiftI18n } from '../../i18n/ShiftI18nContext';
import {
  fetchTreeProgress,
  fetchTeamAnomalies, patchAnomaly, fetchActiveAnnouncements,
  fetchMyMonthAssignments, fetchMyAvailability,
} from '../../lib/shiftApi';
import { ShiftHeaderBar, pctColor, ShiftTabBadge } from './ShiftChrome';
import ShiftTaskTree from './ShiftTaskTree';
import ShiftExtraCreate from './ShiftExtraCreate';
import { TreeTab } from '../ShiftAdminHub';
import ShiftAnnouncements from './ShiftAnnouncements';
import ShiftAnomalyPanel from './ShiftAnomalyPanel';
import InventoryOrderView from '../InventoryOrderView';
import SalesConsumptionView from '../SalesConsumptionView';
import SalesRevenueView from '../SalesRevenueView';

const InventoryCheck = lazy(() => import('../InventoryCheck'));
const PROGRESS_SHIFTS = ['early', 'late', 'night', 'maintenance'];

function currentMonthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function AnomalyCard({ a, isOwner, onPatch }) {
  const { t } = useShiftI18n();
  const title = a?.title || a?.Title || t('anomaly.untitled');
  const status = a?.status || a?.Status || '';
  const reporter = a?.reporterName || a?.reporter_name || '';
  const content = a?.content || a?.Content || '';
  const resolvedNote = a?.resolvedNote || a?.RESOLVED_NOTE || '';
  const imageUrls = Array.isArray(a?.imageUrls) ? a.imageUrls : [];
  const id = a?.id ?? a?.ID;
  const [note, setNote] = useState(resolvedNote || '');
  useEffect(() => { setNote(resolvedNote || ''); }, [resolvedNote, id]);
  if (id == null) return null;
  const statusLabel = status === 'resolved'
    ? t('anomaly.statusResolved')
    : status === 'acknowledged'
      ? t('anomaly.statusAcked')
      : t('anomaly.statusPending');
  return (
    <div className="shift-card shift-anomaly-card" style={{ marginBottom: 8, cursor: 'default', padding: '12px 14px' }}>
      <div className="shift-fc-name">{title}</div>
      <div className="shift-fc-meta">{reporter}{status ? ` · ${statusLabel}` : ''}</div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginTop: 6, wordBreak: 'break-word', color: 'var(--text1)' }}>{content}</div>
      {imageUrls.length > 0 && (
        <div className="shift-anom-imgs">
          {imageUrls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="shift-anom-img-link">
              <img src={url} alt="" className="shift-anom-img" />
            </a>
          ))}
        </div>
      )}
      {resolvedNote ? (
        <div className="shift-anomaly-reply">{t('anomaly.ownerReply', { note: resolvedNote })}</div>
      ) : null}
      {isOwner && status !== 'resolved' && (
        <div style={{ marginTop: 10 }}>
          <textarea
            className="shift-login-inp"
            rows={2}
            placeholder={t('anomaly.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {status !== 'acknowledged' && (
              <button type="button" className="shift-mb-btn" onClick={() => onPatch(id, 'acknowledged', note)}>{t('anomaly.ack')}</button>
            )}
            <button type="button" className="shift-mb-btn" onClick={() => onPatch(id, 'resolved', note)}>{t('anomaly.resolve')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 統一主頁：依登入角色顯示分頁
 */
export default function ShiftHome() {
  const {
    isStaff, isOwner, isSupervisor, supModes, bizDate,
    progress, extraProgress, availableShifts,
  } = useShift();
  const { t, lang } = useShiftI18n();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMgr = isSupervisor || isOwner;
  const [tab, setTab] = useState(() => {
    const tb = searchParams.get('tab');
    return tb === 'anomalies' ? 'anomalies' : 'tasks';
  });
  const [invSub, setInvSub] = useState('check');
  const [byShift, setByShift] = useState({});
  const [expandedProg, setExpandedProg] = useState({});
  const [anomalies, setAnomalies] = useState([]);
  const [anomErr, setAnomErr] = useState('');
  const [unackedAnn, setUnackedAnn] = useState(0);
  const [mgrShowAnomForm, setMgrShowAnomForm] = useState(false);
  const [scheduleDayNote, setScheduleDayNote] = useState('');
  const [availBanner, setAvailBanner] = useState(null);

  useEffect(() => {
    let alive = true;
    const pickBanner = async () => {
      const months = [currentMonthKey(0), currentMonthKey(1)];
      try {
        const cur = await fetchMyAvailability(months[0]);
        if (!alive) return;
        if (cur?.ok && cur.canEdit) {
          setAvailBanner({
            month: months[0],
            fillWindow: cur.fillWindow,
            canEdit: cur.canEdit,
            missingCount: cur.missingDays?.length ?? null,
          });
          return;
        }
        const next = await fetchMyAvailability(months[1]);
        if (!alive) return;
        if (next?.ok) {
          setAvailBanner({
            month: months[1],
            fillWindow: next.fillWindow,
            canEdit: next.canEdit,
            missingCount: next.missingDays?.length ?? null,
          });
        } else setAvailBanner(null);
      } catch {
        if (alive) setAvailBanner(null);
      }
    };
    pickBanner();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const tb = searchParams.get('tab');
    if (tb === 'anomalies') {
      setTab('anomalies');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const regularTodoLeft = Math.max(0, (progress?.total || 0) - (progress?.done || 0));
  const extraTodoLeft = Math.max(0, (extraProgress?.total || 0) - (extraProgress?.done || 0));

  const progressShiftList = (availableShifts?.length
    ? availableShifts
    : PROGRESS_SHIFTS).filter((st) => PROGRESS_SHIFTS.includes(st) || availableShifts?.includes(st));

  const loadProgress = useCallback(async () => {
    try {
      const r = await fetchTreeProgress(bizDate || undefined);
      setByShift(r.byShift || {});
    } catch { setByShift({}); }
  }, [bizDate]);

  const loadAnomalies = useCallback(async () => {
    setAnomErr('');
    try {
      const r = await fetchTeamAnomalies(bizDate || undefined);
      const list = Array.isArray(r?.reports)
        ? r.reports
        : Array.isArray(r?.anomalies)
          ? r.anomalies
          : Array.isArray(r?.list)
            ? r.list
            : [];
      setAnomalies(list.filter(Boolean));
    } catch (e) {
      setAnomalies([]);
      setAnomErr(e?.message || t('anomaly.loadFail'));
    }
  }, [bizDate, t]);

  const refreshAnnBadge = useCallback(async () => {
    try {
      const r = await fetchActiveAnnouncements();
      if (r?.ok) setUnackedAnn(r.unackedCount ?? (r.announcements || []).filter((a) => !a.acked).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshAnnBadge();
  }, [refreshAnnBadge]);

  useEffect(() => {
    const d = new Date();
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    fetchMyMonthAssignments(month, 'mine').then((r) => {
      if (r?.ok && r.myDayNote) setScheduleDayNote(r.myDayNote);
      else setScheduleDayNote('');
    }).catch(() => setScheduleDayNote(''));
  }, [bizDate]);

  useEffect(() => {
    if (tab === 'progress' && isMgr) loadProgress();
    if (tab === 'anomalies' && isMgr) loadAnomalies();
  }, [tab, isMgr, loadProgress, loadAnomalies]);

  const onPatchAnomaly = async (id, status, resolvedNote = '') => {
    try {
      await patchAnomaly(id, status, resolvedNote);
      await loadAnomalies();
    } catch {
      alert(t('anomaly.updateFail'));
    }
  };

  const toggleProg = (st) => {
    setExpandedProg((p) => ({ ...p, [st]: !p[st] }));
  };

  const tabLabel = useMemo(() => ({
    tasks: t('home.tab.tasks'),
    extra: t('home.tab.extra'),
    tree: t('home.tab.tree'),
    announcement: t('home.tab.announce'),
    progress: t('home.tab.progress'),
    anomalies: t('home.tab.anomaly'),
    inventory: t('home.tab.inventory'),
  }[tab] || ''), [tab, t]);

  const showSupMode = isMgr && (tab === 'tasks' || tab === 'extra' || tab === 'progress');

  const localeTag = lang === 'en' ? 'en-US' : 'zh-TW';

  return (
    <>
      <ShiftHeaderBar
        showSupMode={showSupMode}
        currentTabLabel={tabLabel}
      />

      <nav className="shift-main-nav" aria-label={t('home.navAria')}>
        <button type="button" className={`shift-nav-btn ${tab === 'tasks' ? 'on' : ''}`} onClick={() => setTab('tasks')}>
          {t('home.tab.tasks')}
          <ShiftTabBadge count={regularTodoLeft} />
        </button>
        <button type="button" className={`shift-nav-btn tab-extra ${tab === 'extra' ? 'on' : ''}`} onClick={() => setTab('extra')}>
          {t('home.tab.extra')}
          <ShiftTabBadge count={extraTodoLeft} />
        </button>
        {isOwner && (
          <button type="button" className={`shift-nav-btn ${tab === 'tree' ? 'on' : ''}`} onClick={() => setTab('tree')}>
            {t('home.tab.tree')}
          </button>
        )}
        <button type="button" className={`shift-nav-btn tab-ann ${tab === 'announcement' ? 'on' : ''}`} onClick={() => setTab('announcement')}>
          {t('home.tab.announce')}
          <ShiftTabBadge count={unackedAnn} />
        </button>
        {isMgr && (
          <button type="button" className={`shift-nav-btn tab-prog ${tab === 'progress' ? 'on' : ''}`} onClick={() => setTab('progress')}>
            {t('home.tab.progress')}
          </button>
        )}
        <button type="button" className={`shift-nav-btn tab-anom ${tab === 'anomalies' ? 'on' : ''}`} onClick={() => setTab('anomalies')}>
          {t('home.tab.anomaly')}
        </button>
        {(isStaff || isSupervisor || isOwner) && (
          <button type="button" className={`shift-nav-btn tab-inv ${tab === 'inventory' ? 'on' : ''}`} onClick={() => setTab('inventory')}>
            {t('home.tab.inventory')}
          </button>
        )}
        <button type="button" className="shift-nav-btn" onClick={() => nav('/shift/availability')}>{t('home.tab.schedule')}</button>
        <button type="button" className="shift-nav-btn" onClick={() => nav('/shift/my-month-schedule')}>{t('home.tab.month')}</button>
      </nav>

      <div className="shift-scroll">
        {availBanner && (
          <div className="shift-card shift-banner-card">
            <span>
              {t('home.availBanner', { month: availBanner.month })}
              {availBanner.fillWindow?.closeAt
                ? t('home.availDeadline', {
                  at: new Date(availBanner.fillWindow.closeAt).toLocaleString(localeTag, {
                    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  }),
                })
                : ''}
              {availBanner.fillWindow?.message ? ` · ${availBanner.fillWindow.message}` : ''}
              {availBanner.missingCount != null && availBanner.missingCount > 0
                ? t('home.availMissing', { n: availBanner.missingCount })
                : ''}
            </span>
            {availBanner.canEdit ? (
              <button type="button" className="shift-mb-btn" onClick={() => nav('/shift/availability')}>{t('home.availGo')}</button>
            ) : null}
          </div>
        )}
        {scheduleDayNote ? (
          <div className="shift-card shift-banner-card shift-banner-note">
            {t('home.todayNote', { note: scheduleDayNote })}
          </div>
        ) : null}
        {tab === 'tasks' && (
          isMgr ? (
            <section className="shift-home-section">
              <ShiftTaskTree shiftType={(supModes || [])[0] || 'early'} mode="all" />
            </section>
          ) : (
            <ShiftTaskTree mode="all" />
          )
        )}

        {tab === 'extra' && (
          <div>
            {isOwner ? <ShiftExtraCreate /> : null}
            {isMgr ? (
              <ShiftTaskTree
                shiftType={(supModes || [])[0] || 'early'}
                mode="extra"
                extraOverview
              />
            ) : (
              <ShiftTaskTree mode="extra" />
            )}
          </div>
        )}

        {tab === 'tree' && isOwner && (
          <TreeTab />
        )}

        {tab === 'announcement' && (
          <ShiftAnnouncements onUnackedChange={setUnackedAnn} />
        )}

        {tab === 'progress' && isMgr && (
          <div>
            {progressShiftList.map((st) => {
              const p = byShift[st] || { pct: 0, done: 0, total: 0 };
              const open = !!expandedProg[st];
              return (
                <div key={st} className="shift-card" style={{ marginBottom: 10, cursor: 'default' }}>
                  <button
                    type="button"
                    className="shift-fc-top"
                    style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                    onClick={() => toggleProg(st)}
                  >
                    <div className="shift-fc-info">
                      <div className="shift-fc-name">{t(`shift.${st}`)} {open ? '▾' : '▸'}</div>
                      <div className="shift-fc-meta">{t('home.doneMeta', { done: p.done, total: p.total })}</div>
                    </div>
                    <div className="shift-fc-pct" style={{ color: pctColor(p.pct) }}>{p.pct}%</div>
                  </button>
                  <div style={{ height: 6, borderRadius: 99, background: 'var(--bg4)', margin: '0 14px 10px', overflow: 'hidden' }}>
                    <div style={{ width: `${p.pct || 0}%`, height: '100%', background: pctColor(p.pct) }} />
                  </div>
                  {open && (
                    <div style={{ padding: '0 6px 12px' }}>
                      <ShiftTaskTree shiftType={st} readOnly mode="all" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'anomalies' && (
          <div>
            {isMgr ? (
              <>
                <div style={{ padding: '8px 14px 0', display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className={`shift-mb-btn ${mgrShowAnomForm ? 'on' : ''}`}
                    style={{ flex: '0 0 auto', border: '1px solid var(--border2)', fontWeight: 700 }}
                    onClick={() => setMgrShowAnomForm((v) => !v)}
                  >
                    {mgrShowAnomForm ? t('anomaly.collapseForm') : t('anomaly.add')}
                  </button>
                </div>
                {mgrShowAnomForm ? <ShiftAnomalyPanel formOnly onSubmitted={loadAnomalies} /> : null}
                <div className="shift-slabel">{t('anomaly.teamToday')}</div>
                {anomErr && <div className="shift-login-err" style={{ padding: '8px 14px' }}>{anomErr}</div>}
                {anomalies.length === 0 && !anomErr && <div className="shift-empty">{t('anomaly.empty')}</div>}
                {anomalies.map((a) => (
                  <AnomalyCard key={a.id || a.ID} a={a} isOwner={isOwner} onPatch={onPatchAnomaly} />
                ))}
              </>
            ) : (
              <ShiftAnomalyPanel />
            )}
          </div>
        )}

        {tab === 'inventory' && (isStaff || isSupervisor) && (
          <Suspense fallback={<div className="shift-empty">{t('home.loadInv')}</div>}>
            <div className="shift-inv-embed">
              <InventoryCheck embedFromShift />
            </div>
          </Suspense>
        )}

        {tab === 'inventory' && isOwner && (
          <>
            <div className="shift-modebar" style={{ marginBottom: 12 }}>
              <button type="button" className={`shift-mb-btn ${invSub === 'check' ? 'on' : ''}`} onClick={() => setInvSub('check')}>{t('home.tab.inventory')}</button>
              <button type="button" className={`shift-mb-btn ${invSub === 'order' ? 'on' : ''}`} onClick={() => setInvSub('order')}>{t('home.tab.order')}</button>
              <button type="button" className={`shift-mb-btn ${invSub === 'sales' ? 'on' : ''}`} onClick={() => setInvSub('sales')}>{t('home.tab.sales')}</button>
              <button type="button" className={`shift-mb-btn ${invSub === 'revenue' ? 'on' : ''}`} onClick={() => setInvSub('revenue')}>{t('home.tab.revenue')}</button>
            </div>
            {invSub === 'check' ? (
              <Suspense fallback={<div className="shift-empty">{t('home.loadInv')}</div>}>
                <div className="shift-inv-embed">
                  <InventoryCheck embedFromShift />
                </div>
              </Suspense>
            ) : invSub === 'sales' ? (
              <div className="shift-inv-embed">
                <SalesConsumptionView auth="shift" compact />
              </div>
            ) : invSub === 'revenue' ? (
              <div className="shift-inv-embed">
                <SalesRevenueView auth="shift" compact />
              </div>
            ) : (
              <div className="shift-inv-embed" style={{ padding: 8, background: '#fff', borderRadius: 12 }}>
                <InventoryOrderView compact />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
